/**
 * Deleting an unused fault type or location from Configuration (TRBUD-448).
 *
 * Eden, 24 October 2026: test fault types and fake terminals are to be "gone,
 * not switched off". The database refuses a delete while ANYTHING points at the
 * row (`delete_fault_type`, `delete_location`), counting every foreign key from
 * the catalogue. This file is the shared half: the request body, the wire
 * shapes, and the plain-English name of each kind of reference, so the dialog
 * and the API's refusal say the same thing.
 *
 * Browser-safe: types, one schema and string functions.
 */
import { idempotencyKey, strictObject } from "./requests.js";

/** The body of both delete routes: an idempotency key and nothing else. */
export const deleteConfigRecordBody = strictObject({ idempotencyKey });

/** One kind of record that points at the row, and how many. Only counts above zero are sent. */
export interface ConfigReference {
  /** The table holding the foreign key, as the database names it. */
  table: string;
  /** The foreign-key column. */
  column: string;
  count: number;
  /** "2 work orders", "1 stock movement": what the dialog prints. */
  label: string;
}

/**
 * What the delete dialog reads before it offers Delete.
 *
 * `refusal` is null when the row can be deleted now, and otherwise the sentence
 * that says why not and what to do instead. It is the server's answer to the
 * same question the delete route asks again, under a lock, when Delete is
 * pressed: the dialog explains, the database enforces.
 */
export interface ConfigDeletionCheck {
  references: ConfigReference[];
  refusal: string | null;
}

/** A location's deletion check, with the two facts the dialog says out loud. */
export interface LocationDeletionCheck extends ConfigDeletionCheck {
  /** It came from the MaintainX import, so deleting it also stops the import re-creating it. */
  imported: boolean;
  /** It has a switched-off worksite boundary, which is deleted with it. */
  hasWorksiteBoundary: boolean;
}

export interface FaultTypeDeleteReceipt {
  reasonTypeId: string;
  name: string;
}

export interface LocationDeleteReceipt {
  locationId: string;
  name: string;
  /** True when an import tombstone was written, so the import cannot bring it back. */
  tombstoned: boolean;
}

/**
 * The name of each kind of reference, singular and plural, keyed
 * `table.column`. A foreign key not listed here (one added after this file) is
 * still counted and still refuses the delete; it is just named by its table.
 */
const REFERENCE_NAMES: Record<string, readonly [string, string]> = {
  // location
  "asset.current_location_id": ["asset currently placed here", "assets currently placed here"],
  "asset.domicile_location_id": ["asset based here", "assets based here"],
  "asset_home_base_map.location_id": ["home-base rule", "home-base rules"],
  "bay_budget_breach.terminal_location_id": ["bay time alert", "bay time alerts"],
  "inventory_txn.location_id": ["stock movement", "stock movements"],
  "part_custody_issue.custody_location_id": ["parts hand-over", "parts hand-overs"],
  "part_custody_issue.from_location_id": ["parts hand-over", "parts hand-overs"],
  "part_custody_return.to_location_id": ["parts return", "parts returns"],
  "purchase_order.deliver_to_location_id": ["purchase order delivered here", "purchase orders delivered here"],
  "stock_count.location_id": ["stock count", "stock counts"],
  "time_clock_exception_request.location_id": ["clock-in exception request", "clock-in exception requests"],
  "time_clock_shift.location_id": ["time clock punch", "time clock punches"],
  "time_clock_site.location_id": ["worksite boundary", "worksite boundaries"],
  "work_order_bay_episode.terminal_location_id": ["bay visit", "bay visits"],
  // reason_type (fault types)
  "maintenance_request.reason_type_id": ["maintenance request", "maintenance requests"],
  "reason_type.parent_id": ["fault type nested under it", "fault types nested under it"],
  "service_program.reason_type_id": ["service program", "service programs"],
  "work_order_fault_link.reason_type_id": ["work order", "work orders"],
  "work_order_issue.reason_type_id": ["ticket issue line", "ticket issue lines"],
};

/** "3 work orders", "1 stock movement", or "2 rows in some_table" for an unnamed key. */
export function describeConfigReference(table: string, column: string, count: number): string {
  const names = REFERENCE_NAMES[`${table}.${column}`];
  if (!names) return `${count} ${count === 1 ? "row" : "rows"} in ${table}`;
  return `${count} ${count === 1 ? names[0] : names[1]}`;
}

/** "2 work orders and 1 maintenance request". Empty input gives "". */
export function joinConfigReferences(references: readonly ConfigReference[]): string {
  const labels = references.map((r) => r.label);
  if (labels.length <= 1) return labels[0] ?? "";
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}
